import { Popover } from "@mui/material";
import * as React from "react";

import { usePopupState } from "@/shared/hooks/usePopupState";

import { Tag } from "../Tag";

import styles from "./TagGroup.module.css";

interface TagGroupProps {
  tags: string[];

  /**
   * Max pills to show before collapsing the remainder into a "+N" indicator.
   * Omit to show every tag.
   */
  maxDisplayCount?: number;

  /** Makes each tag a button, e.g. to filter by it. */
  onClick?: (tag: string) => void;

  /**
   * Filter mode: the tags picked so far. The rest are dimmed. Left out, no
   * tag is dimmed.
   */
  selected?: string[];
}

/**
 * Read-only tags on one line, sorted, with any past `maxDisplayCount` behind a
 * "+N" button that lists them in a popover.
 */
export function TagGroup(props: TagGroupProps) {
  const { tags, maxDisplayCount, onClick, selected } = props;

  const sorted = React.useMemo(() => [...tags].sort(), [tags]);
  const shown =
    maxDisplayCount !== undefined ? sorted.slice(0, maxDisplayCount) : sorted;
  const hidden = sorted.slice(shown.length);
  const overflow = hidden.length;

  const popup = usePopupState();

  const renderTag = (tag: string) => (
    <Tag
      key={tag}
      label={tag}
      onClick={onClick ? () => onClick(tag) : undefined}
      selected={selected ? selected.includes(tag) : undefined}
    />
  );

  return (
    <span className={styles.list}>
      {shown.map(renderTag)}
      {overflow > 0 && (
        <>
          <button
            type="button"
            className={styles.more}
            aria-haspopup="dialog"
            aria-expanded={popup.isOpen}
            aria-label={`Show ${overflow} more ${overflow === 1 ? "tag" : "tags"}`}
            onClick={(event) => {
              // Don't let the click bubble to the grid row (select / navigate).
              event.stopPropagation();
              popup.open({ anchorEl: event.currentTarget });
            }}
          >
            +{overflow}
          </button>
          <Popover
            open={popup.isOpen}
            anchorEl={popup.anchor}
            onClose={popup.close}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            slotProps={{ paper: { className: styles.popover } }}
          >
            <div className={styles.popoverList}>{hidden.map(renderTag)}</div>
          </Popover>
        </>
      )}
    </span>
  );
}
